import { PAGINATION_LIMIT_VALUE_NUM, PAGINATION_NUM_VALUE_NUM } from 'constants/headers'
import { getPaginationHeaders } from 'tools/headers'
import request from 'tools/request'
import { BillingDocumentationResponse, CombinedDocumentationResponse, DocumentListResponse } from 'types/document'
import {
  InvoiceApproveForFundingRequest,
  InvoiceApproveForFundingResponse,
  InvoiceChangelogList,
  InvoiceChangelogParams,
  InvoiceCreateRequest,
  InvoiceCsvResponse,
  InvoiceListParams,
  InvoiceListResponse,
  InvoicePreviewRequest,
  InvoicePreviewResponse,
  InvoiceResponse,
  InvoiceVerifyRequest,
  UpdateInvoiceRequest,
  RejectAllInvoicesRequest,
  InvoicesRebillRequest,
  InvoiceRebillResponse,
  InvoiceLineItemResponse,
  CreateInvoiceLineItemRequest,
  InvoiceListTotalsResponse,
  InvoiceSummaryInfo,
  InvoiceCollectionFeeListResponse,
  InvoiceCollectionFeeListParams,
  ValidateInvoiceNumbersParams,
  ValidateInvoiceNumbersResponse,
  InvoiceAppliedTransactionListResponse,
  OpenCreditInfo,
} from 'types/invoice'
import { AssociateInvoiceLabelResponse, InvoiceLabelCollectionResponse, LabelAssignRequest } from 'types/labels'
import { TermResponse } from 'types/terms'

export const listInvoices = async (
  params: InvoiceListParams,
  pageNumber: number = PAGINATION_NUM_VALUE_NUM,
  pageLimit: number = PAGINATION_LIMIT_VALUE_NUM
) => {
  if (params && params.q) {
    params.q = params.q.trim()
  }
  const data = await request
    .get<InvoiceListResponse>('invoices', {
      params: params,
      headers: getPaginationHeaders(pageNumber, pageLimit, true),
    })
    .then((res) => {
      return {
        headers: res.headers,
        data: res.data.invoices,
      }
    })
  return data
}

export const listInvoicesTotals = async (params: InvoiceListParams) => {
  if (params && params.q) {
    params.q = params.q.trim()
  }
  const data = await request
    .get<InvoiceListTotalsResponse>('invoices/list-totals', {
      params: params,
    })
    .then((res) => res.data.totals)
  return data
}

export const invoicesPreviewInvoiceGroup = async (params: InvoicePreviewRequest) => {
  const data = await request
    .post<InvoicePreviewResponse>('invoices/approve-for-funding-summary', params)
    .then((res) => {
      return res.data
    })
  return data
}

export const createInvoice = async (payload: InvoiceCreateRequest) => {
  return await request.post<InvoiceResponse>('invoices', payload)
}

export const getInvoice = async (invoiceId: string, state: string) => {
  const data = await request
    .get<InvoiceResponse>(`invoices/${invoiceId}`, {
      params: {
        active: state,
      },
    })
    .then((res) => {
      return res.data
    })
  return data
}

export const updateInvoice = async (invoiceId: string, payload: UpdateInvoiceRequest) => {
  const data = await request.put<InvoiceResponse>(`invoices/${invoiceId}`, payload).then((res) => {
    return res.data
  })
  return data
}

export const updateClosedInvoice = async (invoiceId: string, payload: UpdateInvoiceRequest) => {
  const data = await request.put<InvoiceResponse>(`invoices/closed-invoice/${invoiceId}`, payload).then((res) => {
    return res.data
  })
  return data
}

export const rejectAllInvoices = async (payload: RejectAllInvoicesRequest) => {
  const data = await request.patch<InvoiceResponse>('invoices/reject-invoices', payload)
  return data
}

export const addDocumentToInvoice = async (invoiceId: String, documents?: string[]) => {
  const data = await request
    .post<InvoiceResponse>(`invoices/${invoiceId}/documents`, {
      documentIds: documents,
    })
    .then((res) => {
      return res.data
    })
  return data
}

export const invoiceApproveForFunding = async (payload: InvoiceApproveForFundingRequest) => {
  const data = await request
    .patch<InvoiceApproveForFundingResponse>(`invoices/approve-for-funding`, payload)
    .then((res) => {
      return res.data
    })
  return data
}

export const invoiceVerify = async (payload: InvoiceVerifyRequest) => {
  const data = await request.patch<InvoiceResponse>('invoices/verification', payload)
  return data
}

export const getInvoiceChangelogList = async (
  params: InvoiceChangelogParams,
  pageNumber: number = PAGINATION_NUM_VALUE_NUM,
  pageLimit?: number
) => {
  const data = await request
    .get<InvoiceChangelogList>(`invoices/${params.invoiceId}/changelogs`, {
      params: params,
      headers: getPaginationHeaders(pageNumber, pageLimit),
    })
    .then((res) => {
      return res
    })
  return data
}

export const uploadCsv = async (inputFile: File[]) => {
  const formData = new FormData()
  formData.append('inputFile', inputFile[0])
  const data = await request.post<InvoiceCsvResponse>('invoices/csv-upload', formData).then((res) => {
    return res.data
  })
  return data
}

export const getInvoiceTermInfo = async (companyClientOrBrokerId: string, companyDebtorId: string) => {
  const data = await request
    .get<TermResponse>(`invoices/term-info`, {
      params: { companyClientOrBrokerId, companyDebtorId },
    })
    .then((res) => {
      return res.data.term
    })
  return data
}

export const deleteInvoice = async (invoiceId: string) => {
  const data = await request.delete(`invoices/${invoiceId}`).then((res) => {
    return res.data
  })
  return data
}

export const getInvoiceLabels = async (invoiceId: string) => {
  const data = await request.get<InvoiceLabelCollectionResponse>(`invoices/${invoiceId}/labels`).then((res) => {
    return res.data
  })
  return data
}

export const removeLabelFromInvoice = async (invoiceId: string, labelId: string) => {
  const data = await request
    .patch<InvoiceLabelCollectionResponse>(`invoices/${invoiceId}/labels/${labelId}`)
    .then((r) => r.data)
  return data
}

export const exportInvoices = async (params: InvoiceListParams) => {
  const data = await request
    .get<string>('invoices/export', {
      params: params,
    })
    .then((res) => {
      return res.data
    })
  return data
}

export const rebillInvoices = async (params: InvoicesRebillRequest) => {
  const data = await request.put<InvoiceRebillResponse>('invoices/rebill', params).then((res) => {
    return {
      headers: res.headers,
      data: res.data,
    }
  })
  return data
}

export const associateInvoiceLabel = async (invoiceId: string, payload: LabelAssignRequest) => {
  const data = await request
    .patch<AssociateInvoiceLabelResponse>(`invoices/${invoiceId}/labels`, payload)
    .then((res) => {
      return res.data
    })
  return data
}

export const getInvoiceDocuments = async (
  invoiceId: string,
  state: string,
  pageNumber: number = PAGINATION_NUM_VALUE_NUM,
  pageLimit: number = PAGINATION_LIMIT_VALUE_NUM,
  sort: string
) => {
  const data = await request
    .get<DocumentListResponse>(`invoices/${invoiceId}/documents`, {
      params: {
        active: state,
        sort: sort,
      },
      headers: getPaginationHeaders(pageNumber, pageLimit),
    })
    .then((res) => {
      return { data: res.data, headers: res.headers }
    })
  return data
}

export const getInvoiceCombinedDocumentation = async (invoiceId: string) => {
  const data = await request
    .get<CombinedDocumentationResponse>(`invoices/${invoiceId}/combined-documentation`)
    .then((res) => {
      return res.data
    })
  return data
}

export const getInvoiceBillingDocumentation = async (invoiceId: string) => {
  const data = await request
    .get<BillingDocumentationResponse>(`invoices/${invoiceId}/billing-documentation`)
    .then((res) => {
      return res.data
    })
  return data
}

// INVOICE LINE ITEMS

export const listInvoiceLineItems = async (
  invoiceId: string,
  q: string,
  pageNumber: number = PAGINATION_NUM_VALUE_NUM,
  pageLimit: number = PAGINATION_LIMIT_VALUE_NUM,
  sort: string
) => {
  const data = await request
    .get<InvoiceLineItemResponse>(`invoices/${invoiceId}/line-items`, {
      params: {
        q: q,
        sort: sort,
      },
      headers: getPaginationHeaders(pageNumber, pageLimit),
    })
    .then((res) => {
      return {
        headers: res.headers,
        data: res.data.lineItems,
      }
    })
  return data
}

export const createInvoiceLineItem = async (invoiceId: string, payload: CreateInvoiceLineItemRequest) => {
  return await request.post<InvoiceLineItemResponse>(`invoices/${invoiceId}/line-items`, payload)
}

export const deleteInvoiceLineItem = async (invoiceId: string, lineItemId: string) => {
  return await request.delete(`invoices/${invoiceId}/line-items/${lineItemId}`)
}

export const updateInvoiceLineItem = async (
  invoiceId: string,
  lineItemId: string,
  payload: CreateInvoiceLineItemRequest
) => {
  return await request.put<InvoiceLineItemResponse>(`invoices/${invoiceId}/line-items/${lineItemId}`, payload)
}

export const exportInvoiceLineItems = async (invoiceId: string, q: string, sort: string) => {
  const data = await request
    .get<string>(`invoices/${invoiceId}/line-items/export`, {
      params: {
        q: q,
        sort: sort,
      },
    })
    .then((res) => {
      return res.data
    })
  return data
}

export const exportInvoicesCollections = async (params: InvoiceListParams) => {
  const data = await request
    .get<string>('invoices/export/collections', {
      params: params,
    })
    .then((res) => {
      return res.data
    })
  return data
}

export const getIneligibleDisputedInvoices = async (
  companyId: string,
  isDisputed: boolean = true,
  isIneligible: boolean = true
): Promise<InvoiceSummaryInfo[]> => {
  const data = await request
    .get<InvoiceListResponse>(`invoices/${companyId}/ineligible-disputed`, {
      params: {
        isDisputed,
        isIneligible,
      },
    })
    .then((res) => {
      return res.data.invoices
    })
  return data
}

export const getOpenCredits = async (companyId: string): Promise<OpenCreditInfo[]> => {
  const data = await request.get<OpenCreditInfo[]>(`invoices/${companyId}/open-credits`).then((res) => {
    return res.data
  })
  return data
}

export const getInvoiceCollectionFeesFromInvoice = async (
  invoiceId: string,
  params: InvoiceCollectionFeeListParams,
  pageNumber: number = PAGINATION_NUM_VALUE_NUM,
  pageLimit: number = PAGINATION_LIMIT_VALUE_NUM
) => {
  const data = await request
    .get<InvoiceCollectionFeeListResponse>(`invoices/${invoiceId}/collection-fees`, {
      params: params,
      headers: getPaginationHeaders(pageNumber, pageLimit),
    })
    .then((res) => {
      return {
        headers: res.headers,
        data: res.data.invoiceCollectionFees,
      }
    })
  return data
}

export const exportInvoiceDocuments = async (payload: { invoiceId: string; documentIds: string[]; targetEmails: string }) => {
  const { data } = await request.post('invoices/documents/export', payload)
  return data
}

export const validateInvoiceNumbers = async (params: ValidateInvoiceNumbersParams) => {
  const data = await request
    .get<ValidateInvoiceNumbersResponse>('invoices/validate-numbers', {
      params: params,
    })
    .then((res) => {
      return res.data
    })
  return data
}

export const getInvoiceAppliedTransactions = async (invoiceId: string) => {
  const data = await request
    .get<InvoiceAppliedTransactionListResponse>(`invoices/${invoiceId}/applied-transactions`)
    .then((res) => {
      return res.data.appliedTransactions
    })
  return data
}
